import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from '@modules/shared/layout/layout/layout.component';
import { filter } from 'rxjs';
import { FontObserverService } from 'services/font-observer/font-observer.service';
import { THEMES } from 'theme/themes';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TabContextService } from '@modules/auth/services/tab-context.service';
import { EmissionsNoticeService } from '@modules/shared/modal-emissions-notice/emissions-notice.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LayoutComponent, MatProgressSpinnerModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  title = 'carnival-lleego-app';
  themeToApply = THEMES['default'];
  fontsLoaded = false;

  constructor(
    private router: Router,
    private cd: ChangeDetectorRef,
    private fontObserver: FontObserverService,
    @Inject(DOCUMENT) private document: Document,
    private tabCtx: TabContextService,
    private emissionsNotice: EmissionsNoticeService,
  ) {
    this.updateTitleAndFavicon();
  }

  private currentUserId(): string | undefined {
    const raw = localStorage.getItem('user');
    if (!raw) return undefined;
    try {
      return JSON.parse(raw)?._id;
    } catch {
      return undefined;
    }
  }

  private registerInheritTicketListeners() {
    const writer = () => this.tabCtx.writeInheritTicket(this.currentUserId());
    document.addEventListener(
      'mousedown',
      (e: MouseEvent) => {
        if (e.button === 1 || e.ctrlKey || e.metaKey) writer();
      },
      true,
    );
    document.addEventListener('contextmenu', () => writer(), true);
  }

  private registerCrossTabLogoutListener() {
    window.addEventListener('storage', (e: StorageEvent) => {
      const isLogoutEvent =
        e.key === null || (e.key === 'token' && e.newValue === null);
      if (!isLogoutEvent) return;
      sessionStorage.clear();
      window.location.href = '/auth';
    });
  }

  updateTitleAndFavicon() {
    const url = window.location.hostname;
    const titleElement = this.document.querySelector('title');
    const faviconElement = this.document.querySelector(
      "link[rel='icon']",
    ) as HTMLLinkElement;

    switch (true) {
      case url.includes('grupo8-v1.flaptek.com'):
        titleElement!.textContent = 'Grupo 8 Operadora de Turismo | Aéreos';
        faviconElement.href = 'assets/favicongrupo8.ico';
        this.themeToApply = THEMES['grupo8'];
        break;
      case url.includes('app.dev.emissions.consolidator.tourboplus.com'):
        titleElement!.textContent = 'Grupo 8 Operadora de Turismo | Aéreos';
        faviconElement.href = 'assets/favicongrupo8.ico';
        this.themeToApply = THEMES['grupo8'];
        break;
      case url.includes('jump-v1.flaptek.com'):
        titleElement!.textContent = 'Jump | Aéreos';
        faviconElement.href = 'assets/faviconjump.ico';
        break;
      case url.includes('nites-v1.flaptek.com'):
        titleElement!.textContent = 'Nites Travel | Aéreos';
        faviconElement.href = 'assets/faviconnites.ico';
        break;
      case url.includes('traveltips-v1.flaptek.com'):
        titleElement!.textContent = 'Traveltips | Aéreos';
        faviconElement.href = 'assets/favicontraveltips.ico';
        break;
      case url.includes('felizviaje-v1.flaptek.com'):
        titleElement!.textContent = 'Feliz viaje | Aéreos';
        faviconElement.href = 'assets/faviconfelizviaje.ico';
        break;
      case url.includes('betheworld-v1.flaptek.com'):
        titleElement!.textContent = 'Be the world | Aéreos';
        faviconElement.href = 'assets/faviconbetheworld.ico';
        break;
      case url.includes('kamchatka-v1.flaptek.com'):
        titleElement!.textContent = 'Kamchatka Viajes y Turismo | Aéreos';
        faviconElement.href = 'assets/faviconkamchatka.ico';
        break;
      case url.includes('aereos.sumatraviajes.com'):
        titleElement!.textContent = 'Sumatra Viajes y Turismo | Aéreos';
        faviconElement.href = 'assets/faviconsumatra.ico';
        break;
      case url.includes('aereos.flaptek.com'):
        titleElement!.textContent = 'Flaptek | Aéreos';
        faviconElement.href = 'assets/faviconFlaptek.ico';
        break;
      case url.includes('servicios.snuggle.travel'):
        titleElement!.textContent = 'Snuggle | Aéreos';
        faviconElement.href = 'assets/faviconSnuggle.ico';
        break;
      case url.includes('helius-v1.flaptek.com'):
        titleElement!.textContent = 'Helius | Aéreos';
        faviconElement.href = 'assets/faviconHelius.ico';
        break;
      case url.includes('easywings-v1.flaptek.com'):
        titleElement!.textContent = 'Easywings | Mediterránea Turismo';
        faviconElement.href = 'assets/faviconMediterranea.ico';
        break;
      case url.includes('planisferio-v1.flaptek.com'):
        titleElement!.textContent = 'Planisferio | Aéreos';
        faviconElement.href = 'assets/faviconPlanisferio.ico';
        break;
      default:
        break;
    }
    this.applyTheme(this.themeToApply);
  }
  ngOnInit() {
    this.registerInheritTicketListeners();
    this.registerCrossTabLogoutListener();
    this.getCurrentRoute();
    this.fontObserver.fontsLoaded$.subscribe((loaded) => {
      this.fontsLoaded = loaded;
    });
  }
  getCurrentRoute() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url !== '/') this.cd.detectChanges();
        // Aviso de Emisiones (CAT-5153): primera pantalla tras el login.
        if (!event.urlAfterRedirects.startsWith('/auth')) {
          this.emissionsNotice.showIfPending();
        }
      });
  }

  applyTheme(theme: Partial<Record<string, string>>) {
    Object.entries(theme).forEach(([key, value]) => {
      if (value !== undefined) {
        document.documentElement.style.setProperty(key, value);
      }
    });
  }
}
